import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building2, FileUp, Pencil, Plus, Power, Search, Wand2 } from 'lucide-react'
import { opcoApi } from '../../services/api'
import { useOpCo } from '../../context/OpCoContext'
import { useToast } from '../../context/ToastContext'
import OpCoFormModal from '../../components/opco/admin/OpCoFormModal'
import OpCoImportModal from '../../components/opco/admin/OpCoImportModal'
import MembersPanel from '../../components/opco/admin/MembersPanel'
import DomainsPanel from '../../components/opco/admin/DomainsPanel'
import NetSuitePanel from '../../components/opco/admin/NetSuitePanel'
import BcConnectionsPanel from '../../components/settings/BcConnectionsPanel'
import { useFeatures } from '../../contexts/FeaturesContext'
import UnassignedBanner from '../../components/opco/admin/UnassignedBanner'
import AdminsPanel from '../../components/opco/admin/AdminsPanel'
import AutoAssignPanel from '../../components/opco/admin/autoassign/AutoAssignPanel'
import {
  ConfirmDialog, PanelState, asList, cardCls, errorMessage, inputCls, primaryBtn, secondaryBtn,
} from '../../components/opco/admin/shared'

const STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'all', label: 'All' },
]

function normalizeOpco(o) {
  return {
    ...o,
    id: o.id ?? o.opco_id,
    code: o.code,
    name: o.name || o.code,
    is_active: o.is_active !== false,
  }
}

// OpCos tab of Users & Groups.
// OpCo admins: every OpCo, create / rename / deactivate, members with roles,
// domain and mailbox mappings, unassigned count.
// OpCo managers: only the OpCos they manage, members add/remove only.
export default function OpCos({ activeTab, onTabChange, tabs = [] }) {
  const toast = useToast()
  const { isOpcoAdmin, opcos: myOpcos } = useOpCo()
  const features = useFeatures()

  const managedCodes = useMemo(
    () => new Set((myOpcos || []).filter((o) => o.role === 'manager').map((o) => o.code.toLowerCase())),
    [myOpcos],
  )

  const [opcos, setOpcos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [selectedId, setSelectedId] = useState(null)
  const [formFor, setFormFor] = useState(null) // null | 'new' | opco
  const [importing, setImporting] = useState(false)
  const [toggling, setToggling] = useState(null)
  const [toggleBusy, setToggleBusy] = useState(false)
  // null until a Super Admin picks one: Auto-assign is the front door once an
  // OpCo exists (a rule needs an OpCo to point at); manual OpCos & members is
  // for exceptions and for the very first OpCo.
  const [view, setView] = useState(null) // null | 'opcos' | 'auto' (Super Admins only)
  const [membersEpoch, setMembersEpoch] = useState(0) // bumped on return from Auto-assign: members may have changed

  const [domains, setDomains] = useState([])
  const [domainsLoading, setDomainsLoading] = useState(false)
  const [domainsError, setDomainsError] = useState(null)

  const loadOpcos = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (isOpcoAdmin) {
        const res = await opcoApi.list({ includeInactive: true })
        setOpcos(asList(res?.data, 'opcos').map(normalizeOpco))
      } else {
        // Managers: only their own OpCos. The server scopes the list too; this
        // keeps the screen right even if it returns more.
        let list
        try {
          const res = await opcoApi.list()
          list = asList(res?.data, 'opcos').map(normalizeOpco)
        } catch {
          list = (myOpcos || []).map((o) => normalizeOpco({ ...o, id: o.opco_id }))
        }
        setOpcos(list.filter((o) => managedCodes.has(String(o.code).toLowerCase()) && o.id != null))
      }
    } catch (err) {
      setError(errorMessage(err, 'Failed to load OpCos'))
    } finally {
      setLoading(false)
    }
  }, [isOpcoAdmin, myOpcos, managedCodes])

  const loadDomains = useCallback(async () => {
    if (!isOpcoAdmin) return
    setDomainsLoading(true)
    setDomainsError(null)
    try {
      const res = await opcoApi.listDomains()
      setDomains(asList(res?.data, 'domains'))
    } catch (err) {
      setDomainsError(errorMessage(err, 'Failed to load mappings'))
    } finally {
      setDomainsLoading(false)
    }
  }, [isOpcoAdmin])

  useEffect(() => { loadOpcos() }, [loadOpcos])
  useEffect(() => { loadDomains() }, [loadDomains])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return opcos
      .filter((o) => !isOpcoAdmin || statusFilter === 'all' || (statusFilter === 'active' ? o.is_active : !o.is_active))
      .filter((o) => !q || `${o.code} ${o.name}`.toLowerCase().includes(q))
      .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  }, [opcos, search, statusFilter, isOpcoAdmin])

  // Keep a valid selection: the first visible OpCo when none (or a hidden one) is selected.
  useEffect(() => {
    if (!visible.some((o) => o.id === selectedId)) setSelectedId(visible[0]?.id ?? null)
  }, [visible, selectedId])

  const selected = opcos.find((o) => o.id === selectedId) || null
  const activeView = !isOpcoAdmin ? 'opcos' : (view ?? (!loading && opcos.length > 0 ? 'auto' : 'opcos'))

  const saveForm = async (data) => {
    if (formFor === 'new') {
      const res = await opcoApi.create(data)
      const body = res?.data && typeof res.data === 'object' ? res.data : {}
      const created = body.opco || body
      toast.success('OpCo created', `${data.code} (${data.name}) is ready. Add a manager and its members next.`)
      if (body.rules_failed?.length) {
        toast.error('Some emails were not saved', `${body.rules_failed.join(', ')} already belong to another OpCo.`)
      }
      setFormFor(null)
      setStatusFilter('active')
      await loadOpcos()
      if (created.id != null) setSelectedId(created.id)
    } else {
      await opcoApi.update(formFor.id, data)
      toast.success('OpCo renamed', `${formFor.code} is now "${data.name}"`)
      setFormFor(null)
      await loadOpcos()
    }
  }

  const onImported = async ({ result }) => {
    const created = result?.created?.length || 0
    toast.success('OpCos imported', `${created} new, ${result?.renamed || 0} renamed, ${result?.mappings_added || 0} domains/mailboxes added`)
    setImporting(false)
    setStatusFilter('active')
    await Promise.all([loadOpcos(), loadDomains()])
  }

  const confirmToggle = async () => {
    const target = toggling
    setToggleBusy(true)
    try {
      await opcoApi.update(target.id, { is_active: !target.is_active })
      toast.success(
        target.is_active ? 'OpCo deactivated' : 'OpCo reactivated',
        `${target.code} ${target.is_active ? 'is now inactive' : 'is active again'}`,
      )
      setToggling(null)
      await loadOpcos()
    } catch (err) {
      toast.error('Update failed', errorMessage(err))
    } finally {
      setToggleBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">OpCos</h1>
              <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {opcos.length}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isOpcoAdmin
                ? 'Manage operating companies, their members, and how new requests are routed to them.'
                : 'Manage the members of the OpCos you look after.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tabs.length > 0 && typeof onTabChange === 'function' && (
            <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 shadow-sm ring-1 ring-gray-100 dark:ring-gray-800/80">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => onTabChange(tab.key)}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-navy-800 text-white shadow-sm ring-1 ring-navy-900/20'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    aria-pressed={isActive}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {tab.icon && <tab.icon className="h-4 w-4" />}
                    {tab.label}
                  </button>
                )
              })}
            </div>
          )}
          {isOpcoAdmin && (
            <button type="button" onClick={() => setImporting(true)} className={`${secondaryBtn} h-11 px-5 rounded-xl`}>
              <FileUp className="w-4 h-4" />
              Import CSV
            </button>
          )}
          {isOpcoAdmin && (
            <button
              type="button"
              onClick={() => setFormFor('new')}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-navy-800 text-white text-sm font-semibold shadow-sm ring-1 ring-navy-900/20 transition-all duration-200 hover:bg-navy-900 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:scale-[0.98]"
            >
              <Plus className="w-4 h-4" />
              Add OpCo
            </button>
          )}
        </div>
      </div>

      {isOpcoAdmin && <UnassignedBanner />}

      {isOpcoAdmin && (
        <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5" role="group" aria-label="OpCo section">
          {[{ key: 'auto', label: 'Auto-assign', icon: Wand2 }, { key: 'opcos', label: 'OpCos & members (manual)', icon: Building2 }].map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => {
                if (v.key === 'opcos' && activeView !== 'opcos') setMembersEpoch((n) => n + 1)
                setView(v.key)
              }}
              aria-pressed={activeView === v.key}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold ${
                activeView === v.key
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <v.icon className="h-3.5 w-3.5" /> {v.label}
            </button>
          ))}
        </div>
      )}

      {isOpcoAdmin && activeView === 'auto' && <AutoAssignPanel opcos={opcos} />}

      <div className={`grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] ${isOpcoAdmin && activeView === 'auto' ? 'hidden' : ''}`}>
        {/* OpCo list */}
        <section className={`${cardCls} self-start`} aria-label="OpCo list">
          <div className="space-y-3 border-b border-gray-100 dark:border-gray-800 p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code or name"
                aria-label="Search OpCos"
                className={`${inputCls} pl-9`}
              />
            </div>
            {isOpcoAdmin && (
              <div className="inline-flex w-full rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5" role="group" aria-label="Status filter">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setStatusFilter(f.key)}
                    aria-pressed={statusFilter === f.key}
                    className={`flex-1 rounded-md px-2 py-1 text-xs font-semibold ${
                      statusFilter === f.key
                        ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <PanelState
            loading={loading}
            error={error}
            onRetry={loadOpcos}
            empty={visible.length === 0}
            emptyText={
              opcos.length === 0
                ? (isOpcoAdmin ? 'No OpCos yet. Add the first one to start.' : 'You do not manage any OpCo.')
                : 'No OpCos match.'
            }
          >
            <ul className="max-h-[28rem] overflow-y-auto p-2">
              {visible.map((o) => {
                const isSel = o.id === selectedId
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(o.id)}
                      aria-current={isSel ? 'true' : undefined}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${
                        isSel ? 'bg-navy-50 ring-1 ring-navy-200 dark:bg-navy-800/40 dark:ring-navy-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">{o.code}</span>
                      <span className="flex-1 truncate text-sm text-gray-900 dark:text-gray-100">{o.name}</span>
                      {!o.is_active && <span className="text-[11px] font-semibold text-gray-400">Inactive</span>}
                    </button>
                  </li>
                )
              })}
            </ul>
          </PanelState>
        </section>

        {/* Detail */}
        <div className="space-y-6 min-w-0">
          {selected ? (
            <>
              <div className={`${cardCls} flex flex-wrap items-center gap-3 px-5 py-4`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">{selected.code}</span>
                    <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">{selected.name}</h2>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      selected.is_active
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                    >
                      {selected.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                {isOpcoAdmin && (
                  <>
                    <button type="button" onClick={() => setFormFor(selected)} className={secondaryBtn}>
                      <Pencil className="h-4 w-4" /> Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => setToggling(selected)}
                      className={selected.is_active ? secondaryBtn : primaryBtn}
                    >
                      <Power className="h-4 w-4" /> {selected.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </>
                )}
              </div>
              <MembersPanel
                key={`m-${selected.id}-${membersEpoch}`}
                opco={selected}
                canManageRoles={isOpcoAdmin}
                onShowAutoAssign={isOpcoAdmin ? () => setView('auto') : undefined}
              />
              {isOpcoAdmin && (
                <DomainsPanel
                  key={`d-${selected.id}`}
                  opco={selected}
                  domains={domains}
                  loading={domainsLoading}
                  error={domainsError}
                  onReload={loadDomains}
                />
              )}
              {isOpcoAdmin && (features.netsuite || features.business_central) && (
                <section className={cardCls} aria-label="Integrations">
                  <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Integrations</h2>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Optional. Open one to add the ERP credentials this OpCo posts with.
                    </p>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {features.netsuite && <NetSuitePanel key={`ns-${selected.id}`} opco={selected} />}
                    {features.business_central && <BcConnectionsPanel key={`bc-${selected.id}`} opco={selected} />}
                  </div>
                </section>
              )}
            </>
          ) : (
            !loading && !error && (
              <div className={`${cardCls} px-5 py-12 text-center text-sm text-gray-500 dark:text-gray-400`}>
                Select an OpCo to see its members{isOpcoAdmin ? ' and mappings' : ''}.
              </div>
            )
          )}
        </div>
      </div>

      {isOpcoAdmin && activeView === 'opcos' && <AdminsPanel />}

      {formFor && (
        <OpCoFormModal
          opco={formFor === 'new' ? null : formFor}
          onSubmit={saveForm}
          onClose={() => setFormFor(null)}
        />
      )}
      {importing && <OpCoImportModal onImported={onImported} onClose={() => setImporting(false)} />}
      {toggling && (
        <ConfirmDialog
          title={toggling.is_active ? `Deactivate ${toggling.code}?` : `Reactivate ${toggling.code}?`}
          message={toggling.is_active
            ? `${toggling.name} disappears from the workspace switcher and its members can no longer sign in to it. Its requests and settings are kept, and you can reactivate it later.`
            : `${toggling.name} becomes available again to its members.`}
          confirmLabel={toggling.is_active ? 'Deactivate' : 'Reactivate'}
          danger={toggling.is_active}
          busy={toggleBusy}
          onConfirm={confirmToggle}
          onCancel={() => setToggling(null)}
        />
      )}
    </div>
  )
}
